import * as Sentry from "@sentry/react";

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  Observable,
  ObservableSubscription,
} from "@apollo/client";
import {
  selectAuthProvider,
  selectIsLoggedIn,
} from "../../../../src/redux/app";
import { useMemo, useRef } from "react";

import { CLINIC_EDIT_REFUSAL_MESSAGES } from "../clinicEditWindowMessages";
import { RetryLink } from "@apollo/client/link/retry";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { datadogRum } from "@datadog/browser-rum";
import { debugLog } from "./debugLog";
import { onError } from "@apollo/client/link/error";
import { scrubData } from "@verse_core/shared_components/utils/sentryScrub";
import { store } from "../../../../src/redux/reduxStore";
import { useAuth0 } from "@auth0/auth0-react";

// Auth failures (expired token, mid-refresh race, signed-out tab) — expected
// runtime states, not bugs. Backend messages defined in services/orders/auth.py.
// Auth0 silent-refresh handles the redirect path in authLink below; here we
// just suppress the Sentry noise.
//
// Clinic edit window refusals (window closed, already edited, status locked)
// are expected too: the backend logs them as clinicEditRefused instead.
const SKIP_ALERT_MESSAGES = [
  "User is unauthenticated",
  "User is not authenticated",
  ...CLINIC_EDIT_REFUSAL_MESSAGES,
];

/** Whether a GraphQL error is an expected state that shouldn't alert Sentry. */
export function isExpectedGraphQLError(message: string): boolean {
  return SKIP_ALERT_MESSAGES.some((m) => message.startsWith(m));
}

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      if (isExpectedGraphQLError(error.message)) {
        return;
      }
      // Wrap the plain GraphQL error object in a real Error so Sentry can
      // fingerprint by operation + path instead of bucketing every GraphQL
      // failure under the nearest stack frame name ("forEach").
      const wrapped = new Error(
        `GraphQL error in ${operation.operationName}: ${error.message}`,
      );
      Sentry.captureException(wrapped, {
        fingerprint: [
          "graphql",
          operation.operationName,
          error.path?.join(".") ?? "",
        ],
        tags: {
          errorType: "graphql",
          operation: operation.operationName,
        },
        extra: {
          operationName: operation.operationName,
          variables: operation.variables,
          path: error.path,
          extensions: error.extensions,
          originalMessage: error.message,
        },
      });
    });
  }

  if (networkError) {
    Sentry.captureException(networkError, {
      tags: {
        errorType: "network",
        operation: operation.operationName,
      },
      extra: {
        operationName: operation.operationName,
        variables: operation.variables,
        networkErrorCause: networkError.cause,
        networkErrorName: networkError.name,
      },
    });

    datadogRum.addAction("network_error", {
      operationName: operation.operationName,
      variables: scrubData(operation.variables),
      networkErrorCause: networkError.cause,
      networkErrorMessage: networkError.message,
      networkErrorName: networkError.name,
      networkErrorStack: networkError.stack,
    });
  }
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 10000,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      if (!!error && operation.operationName === "CREATE_ORDER") {
        datadogRum.addAction("retry_order_submission", {
          retry: true,
          operationName: operation.operationName,
          variables: scrubData(operation.variables),
          error: JSON.stringify(error),
          errorName: error.name,
          protocolErrors: JSON.stringify(error.protocolErrors),
          clientErrors: JSON.stringify(error.clientErrors),
          networkError: JSON.stringify(error.networkError),
        });
      }

      // Retry on network errors
      return !!error && operation.operationName === "CREATE_ORDER";
    },
  },
});

const uploadLink = createUploadLink({
  headers: {
    "Apollo-Require-Preflight": "true",
  },
});

export function ApolloWrapper({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  // Ref so the memoized link reads live auth state without recreating the client.
  const auth0StateRef = useRef({ isAuthenticated, isLoading });
  auth0StateRef.current = { isAuthenticated, isLoading };

  const client = useMemo(() => {
    // [DEBUG-1] Track Apollo client recreation
    debugLog("[DEBUG-1] ApolloClient RECREATED");
    const authLink = new ApolloLink((operation, forward) => {
      return new Observable((observer) => {
        let handle: ObservableSubscription | null = null;

        Promise.resolve(operation)
          .then(async (operation) => {
            // Non-Auth0 (password) users have no cached token, so each call would
            // hit Auth0's /authorize iframe and burn the tenant's rate limit.
            const { isAuthenticated, isLoading } = auth0StateRef.current;
            if (!isLoading && !isAuthenticated) {
              return;
            }
            try {
              const token = await getAccessTokenSilently({
                timeoutInSeconds: 10,
              });
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  authorization: `Bearer ${token}`,
                },
              }));
            } catch (error) {
              // Leave this commented out to avoid logging errors in the console
              // while user is not authenticated (a majority of users at the moment).
              // console.error("Error getting access token:", error);

              // Check if an Auth0 user's session has expired
              if ((error as { error?: string })?.error === "login_required") {
                const currentState = store.getState();
                const isLoggedIn = selectIsLoggedIn(currentState);
                const authProvider = selectAuthProvider(currentState);
                const isAlreadyOnLoginPage =
                  window.location.href.includes("/password-login");
                if (
                  isLoggedIn &&
                  authProvider === "AUTH0" &&
                  !isAlreadyOnLoginPage
                ) {
                  // Set flag to bypass beforeunload confirmation
                  sessionStorage.setItem("authRedirect", "true");
                  window.location.href = "/password-login?type=provider";
                }
              }

              // Still proceed with request but without token
              operation.setContext(({ headers = {} }) => ({
                headers: headers,
              }));
            }
          })
          .then(() => {
            handle = forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            });
          })
          .catch(observer.error.bind(observer));

        return () => {
          if (handle) handle.unsubscribe();
        };
      });
    });

    const link = ApolloLink.from([
      errorLink,
      authLink,
      retryLink,
      // apollo-upload-client resolves the root @apollo/client (3.7.14), not
      // this workspace's 3.14.0, so its ApolloLink is a different class.
      uploadLink as unknown as ApolloLink,
    ]);

    return new ApolloClient({
      link: link,
      credentials: "include",
      cache: new InMemoryCache(),
    });
  }, [getAccessTokenSilently]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
